import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Star,
  MapPin,
  Briefcase,
  GraduationCap,
  Award,
  Clock,
  Video,
  MessageCircle,
  Phone,
  Check,
  Filter,
  Search,
  Calendar,
  DollarSign,
  Languages,
  BookOpen,
  Trophy,
  Target,
  Zap,
  Heart
} from 'lucide-react';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';

interface Advisor {
  id: string;
  name: string;
  title: string;
  specialties: string[];
  education: string[];
  experience: number;
  rating: number;
  reviewCount: number;
  location: string;
  timezone: string;
  languages: string[];
  pricing: {
    consultation: number;
    package: number;
    hourly: number;
  };
  availability: 'high' | 'medium' | 'low';
  matchScore: number;
  bio: string;
  achievements: string[];
  communicationMethods: ('video' | 'phone' | 'chat')[];
  responseTime: string;
  successStories: {
    count: number;
    highlights: string[];
  };
  image: string;
  verified: boolean;
  topRated: boolean;
}

const advisors: Advisor[] = [
  {
    id: '1',
    name: 'Dr. Sarah Chen',
    title: 'Former MIT Admissions Officer',
    specialties: ['STEM Applications', 'Ivy League Prep', 'Research Mentorship'],
    education: ['PhD Computer Science - MIT', 'MS Engineering - Stanford'],
    experience: 12,
    rating: 4.9,
    reviewCount: 147,
    location: 'Boston, MA',
    timezone: 'EST',
    languages: ['English', 'Mandarin'],
    pricing: {
      consultation: 150,
      package: 2500,
      hourly: 200
    },
    availability: 'medium',
    matchScore: 95,
    bio: 'Former MIT admissions officer with 12+ years of experience helping students achieve their dreams. Specialized in STEM applications and research mentorship.',
    achievements: [
      '500+ successful college admissions',
      'Former MIT Admissions Committee',
      'Published researcher in AI/ML',
      'TEDx speaker on college admissions'
    ],
    communicationMethods: ['video', 'phone', 'chat'],
    responseTime: '< 2 hours',
    successStories: {
      count: 89,
      highlights: [
        '89% acceptance rate to top 20 universities',
        '$2.3M in scholarships secured for students',
        '95% of students accepted to first-choice schools'
      ]
    },
    image: '👩🏻‍💼',
    verified: true,
    topRated: true
  },
  {
    id: '2',
    name: 'Marcus Williams',
    title: 'College Counseling Director',
    specialties: ['Essay Writing', 'Personal Branding', 'Interview Prep'],
    education: ['MA Education - Harvard', 'BA English - Yale'],
    experience: 8,
    rating: 4.8,
    reviewCount: 203,
    location: 'New York, NY',
    timezone: 'EST',
    languages: ['English', 'Spanish'],
    pricing: {
      consultation: 125,
      package: 2000,
      hourly: 175
    },
    availability: 'high',
    matchScore: 88,
    bio: 'Passionate educator with expertise in personal statement writing and interview preparation. Helps students tell their unique stories.',
    achievements: [
      'Former Harvard admissions reader',
      'Award-winning essay coach',
      '300+ students guided to top schools',
      'Published author on college prep'
    ],
    communicationMethods: ['video', 'phone', 'chat'],
    responseTime: '< 1 hour',
    successStories: {
      count: 67,
      highlights: [
        '92% essay improvement rate',
        'Average 15-point increase in application strength',
        '78% acceptance to dream schools'
      ]
    },
    image: '👨🏿‍🏫',
    verified: true,
    topRated: false
  },
  {
    id: '3',
    name: 'Dr. Elena Rodriguez',
    title: 'Medical School Advisor',
    specialties: ['Pre-Med Track', 'Research Opportunities', 'Medical School Prep'],
    education: ['MD - Johns Hopkins', 'PhD Biochemistry - Caltech'],
    experience: 15,
    rating: 4.9,
    reviewCount: 89,
    location: 'Los Angeles, CA',
    timezone: 'PST',
    languages: ['English', 'Spanish', 'Portuguese'],
    pricing: {
      consultation: 175,
      package: 3000,
      hourly: 225
    },
    availability: 'low',
    matchScore: 92,
    bio: 'Practicing physician and researcher specializing in guiding pre-med students through the complex journey to medical school.',
    achievements: [
      'Active medical researcher',
      '90% med school acceptance rate',
      'Former admissions committee member',
      'Mentor at Johns Hopkins'
    ],
    communicationMethods: ['video', 'phone'],
    responseTime: '< 4 hours',
    successStories: {
      count: 45,
      highlights: [
        '90% acceptance to medical school',
        '$1.8M in research grants secured',
        '100% MCAT score improvement'
      ]
    },
    image: '👩🏽‍⚕️',
    verified: true,
    topRated: true
  },
  {
    id: '4',
    name: 'James Park',
    title: 'Scholarship Specialist',
    specialties: ['Financial Aid', 'Scholarship Applications', 'Need-Based Aid'],
    education: ['MBA Finance - Wharton', 'BA Economics - Berkeley'],
    experience: 10,
    rating: 4.7,
    reviewCount: 156,
    location: 'San Francisco, CA',
    timezone: 'PST',
    languages: ['English', 'Korean'],
    pricing: {
      consultation: 100,
      package: 1500,
      hourly: 150
    },
    availability: 'high',
    matchScore: 85,
    bio: 'Financial aid expert who has helped students secure millions in scholarships and navigate complex financial aid processes.',
    achievements: [
      '$5M+ in scholarships secured',
      'Former financial aid officer',
      'Published expert on college financing',
      'Featured on national media'
    ],
    communicationMethods: ['video', 'phone', 'chat'],
    responseTime: '< 30 minutes',
    successStories: {
      count: 123,
      highlights: [
        '$5.2M in total scholarships secured',
        'Average $18K scholarship per student',
        '85% receive significant financial aid'
      ]
    },
    image: '👨🏻‍💼',
    verified: true,
    topRated: false
  },
  {
    id: '5',
    name: 'Prof. Amara Okafor',
    title: 'International Student Advisor',
    specialties: ['International Applications', 'Visa Process', 'Cultural Adaptation'],
    education: ['PhD International Education - Oxford', 'MA Applied Linguistics - Cambridge'],
    experience: 14,
    rating: 4.8,
    reviewCount: 112,
    location: 'London, UK',
    timezone: 'GMT',
    languages: ['English', 'French', 'Yoruba', 'German'],
    pricing: {
      consultation: 120,
      package: 2200,
      hourly: 160
    },
    availability: 'medium',
    matchScore: 79,
    bio: 'International education expert specializing in helping students navigate US college applications from abroad.',
    achievements: [
      'Former Oxford admissions tutor',
      '200+ international placements',
      'Visa success rate: 98%',
      'Cultural integration specialist'
    ],
    communicationMethods: ['video', 'chat'],
    responseTime: '< 3 hours',
    successStories: {
      count: 78,
      highlights: [
        '94% acceptance to target US schools',
        '98% visa approval rate',
        'Students from 45+ countries'
      ]
    },
    image: '👩🏿‍🎓',
    verified: true,
    topRated: true
  }
];

interface FilterOptions {
  specialty: string;
  priceRange: string;
  availability: string;
  location: string;
  rating: number;
}

export const ExpertAdvisorMatcher: React.FC = () => {
  const [selectedAdvisor, setSelectedAdvisor] = useState<Advisor | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterOptions>({
    specialty: '',
    priceRange: '',
    availability: '',
    location: '',
    rating: 0
  });
  const [sortBy, setSortBy] = useState<'match' | 'rating' | 'price' | 'experience'>('match');

  const filteredAdvisors = advisors
    .filter(advisor => {
      const matchesSearch = advisor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           advisor.specialties.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesSpecialty = !filters.specialty || advisor.specialties.includes(filters.specialty);
      const matchesRating = advisor.rating >= filters.rating;

      return matchesSearch && matchesSpecialty && matchesRating;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'match': return b.matchScore - a.matchScore;
        case 'rating': return b.rating - a.rating;
        case 'price': return a.pricing.consultation - b.pricing.consultation;
        case 'experience': return b.experience - a.experience;
        default: return 0;
      }
    });

  const bookConsultation = (advisor: Advisor) => {
    console.log('Booking consultation with:', advisor.name);
    // In a real app, this would open a booking modal or redirect to calendar
  };

  const sendMessage = (advisor: Advisor) => {
    console.log('Sending message to:', advisor.name);
    // In a real app, this would open a messaging interface
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary-500 to-accent-500 text-white">
        <div className="text-center">
          <Users className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Expert Advisor Matching</h2>
          <p className="text-primary-100 text-lg mb-6">
            Connect with top college admissions experts personalized to your goals and needs
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">500+</div>
              <div className="text-sm text-primary-100">Expert Advisors</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">4.8★</div>
              <div className="text-sm text-primary-100">Average Rating</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">92%</div>
              <div className="text-sm text-primary-100">Success Rate</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Search and Filters */}
      <Card>
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between mb-6">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search advisors or specialties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="match">Best Match</option>
              <option value="rating">Highest Rated</option>
              <option value="price">Lowest Price</option>
              <option value="experience">Most Experience</option>
            </select>

            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2"
            >
              <Filter size={16} />
              <span>Filters</span>
            </Button>
          </div>
        </div>

        {/* Filter Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="border-t border-gray-200 pt-6 mt-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Specialty</label>
                  <select
                    value={filters.specialty}
                    onChange={(e) => setFilters({...filters, specialty: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Specialties</option>
                    <option value="STEM Applications">STEM Applications</option>
                    <option value="Essay Writing">Essay Writing</option>
                    <option value="Pre-Med Track">Pre-Med Track</option>
                    <option value="Financial Aid">Financial Aid</option>
                    <option value="International Applications">International Applications</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price Range</label>
                  <select
                    value={filters.priceRange}
                    onChange={(e) => setFilters({...filters, priceRange: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Any Price</option>
                    <option value="under-150">Under $150</option>
                    <option value="150-200">$150 - $200</option>
                    <option value="over-200">Over $200</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Availability</label>
                  <select
                    value={filters.availability}
                    onChange={(e) => setFilters({...filters, availability: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Any Availability</option>
                    <option value="high">High Availability</option>
                    <option value="medium">Medium Availability</option>
                    <option value="low">Limited Availability</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Rating</label>
                  <select
                    value={filters.rating}
                    onChange={(e) => setFilters({...filters, rating: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="0">Any Rating</option>
                    <option value="4.5">4.5+ Stars</option>
                    <option value="4.7">4.7+ Stars</option>
                    <option value="4.8">4.8+ Stars</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>

      {/* Advisor Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredAdvisors.map((advisor, index) => (
          <motion.div
            key={advisor.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card hover className="h-full">
              <div className="flex items-start space-x-4 mb-4">
                <div className="relative">
                  <div className="text-4xl">{advisor.image}</div>
                  {advisor.verified && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <Check size={12} className="text-white" />
                    </div>
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{advisor.name}</h3>
                    <div className="flex items-center space-x-2">
                      {advisor.topRated && (
                        <Badge variant="warning" size="sm">
                          <Star size={12} className="mr-1" />
                          Top Rated
                        </Badge>
                      )}
                      <Badge variant="primary" size="sm">
                        {advisor.matchScore}% Match
                      </Badge>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-2">{advisor.title}</p>

                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                    <div className="flex items-center space-x-1">
                      <Star size={14} className="text-yellow-400 fill-current" />
                      <span>{advisor.rating}</span>
                      <span>({advisor.reviewCount})</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <MapPin size={14} />
                      <span>{advisor.location}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Briefcase size={14} />
                      <span>{advisor.experience}y exp</span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-700 mb-4">{advisor.bio}</p>

              <div className="space-y-3 mb-4">
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">Specialties:</h4>
                  <div className="flex flex-wrap gap-1">
                    {advisor.specialties.map((specialty, idx) => (
                      <Badge key={idx} variant="secondary" size="sm">
                        {specialty}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">Key Achievements:</h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {advisor.achievements.slice(0, 2).map((achievement, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <Star size={12} className="text-accent-500 mt-0.5 flex-shrink-0" />
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm">
                    <div className="font-semibold text-gray-900">
                      ${advisor.pricing.consultation} consultation
                    </div>
                    <div className="text-gray-600">
                      Responds in {advisor.responseTime}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    {advisor.communicationMethods.map((method, idx) => {
                      const icons = { video: Video, phone: Phone, chat: MessageCircle };
                      const Icon = icons[method];
                      return <Icon key={idx} size={16} className="text-gray-500" />;
                    })}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    onClick={() => bookConsultation(advisor)}
                    className="flex-1"
                  >
                    <Calendar size={14} className="mr-2" />
                    Book Call
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => sendMessage(advisor)}
                    className="flex-1"
                  >
                    <MessageCircle size={14} className="mr-2" />
                    Message
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* AI Matching Insights */}
      <Card className="bg-gradient-to-r from-accent-50 to-primary-50">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center">
            <Zap className="w-6 h-6 text-accent-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              AI Matching Insights
            </h3>
            <p className="text-gray-700 mb-4">
              Based on your profile and goals, here's why we recommend these top matches:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <Heart size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  Dr. Sarah Chen matches your STEM interests and research goals perfectly
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <Target size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  Marcus Williams can help strengthen your personal narrative and essays
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <BookOpen size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  Consider booking multiple advisors for comprehensive support
                </span>
              </li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};